import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox, CheckboxChangeEvent, RadioGroup } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { DrawingObjectCategory } from '../../../../../components/drawing-object/types';
import DsuaMenu from '../../../../../components/weather/settings/menus/DsuaMenu';
import WeatherSettings from '../../../../../components/weather/settings/WeatherSettings';
import {
    ChevronDownOutline,
    ChevronRightOutline,
    ClearSunnyOutline,
    ClearSunnySolid,
    FileOutline,
    LinkOutline,
    WarningTriangleOutline
} from '../../../../../icons/icons';
import { FilterGroupType } from '../../../../../types/filter-group';
import { UdViewModel } from '../../../../../udView/udview';
import { SliderWithTooltip } from '../../../../components/inputs/SliderWithTooltip';
import { EventBus, useEvent } from '../../../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import useView, { useWorkspace } from '../../hooks/use-view';
import { useFlyoutHeader } from '../Flyout';
import { HomeFlyoutTypes } from '../types';
import AircraftSettings from './AircraftSettings';
import { AirportLayerSettings } from './AirportLayerSettings';
import { AirwaysSettings } from './AirwaysSettings';
import CustomGeojsonSettings from './CustomGeojsonSettings';
import { DayNightShadingSettings } from './DayNightShadingSettings';
import { FixesSettings } from './FixesSettings';
import './LayersFlyout.css';
import { LayerConfig, LayersNavState, mapStyles, navStateTitles, projectionTypes } from './LayersFlyout.types';
import { LayersSection } from './LayersSection';
import { NavaidsSettings } from './NavaidsSettings';
import { OverwaterAreasSettings } from './OverwaterAreasSettings';
import { TerrainShadingSettings } from './TerrainShadingSettings';

export function LayersFlyout() {
    const { setSubMenu } = useFlyoutHeader();
    const { view, id: workspaceId } = useView();
    const dispatch = useAppDispatch();
    const drawingObjects = useAppSelector((x) => x.drawingObjects.objects);
    const geojsonWarnings = useAppSelector((x) => x.workspace.items[workspaceId].geojsonWarnings);
    const [navState, setNavState] = useState<LayersNavState>(LayersNavState.Layers);
    const [showDrawingObjectsViewBasedMenu, setShowDrawingObjectsViewBasedMenu] = useState(false);
    const [draftRotationSpeed, setDraftRotationSpeed] = useState<number | null>(null);
    const navDataWarning = useWorkspace((x) => x.navDataWarning);

    useEvent(
        'open-weather-settings',
        useCallback(
            (args) => {
                //If we're on a different page change it and re-fire the event so that the weather settings page can handle it
                if (navState !== LayersNavState.WeatherSettings) {
                    setNavState(LayersNavState.WeatherSettings);
                    self.setTimeout(() => {
                        EventBus.fire('open-weather-settings', args);
                    }, 10);
                }
            },
            [navState]
        )
    );

    useEvent(
        'open-flyout',
        useCallback(
            (args) => {
                if (args.type === HomeFlyoutTypes.layers) {
                    const sub = args.sub?.type ?? LayersNavState.Layers;
                    if (navState !== sub) {
                        setNavState(sub);
                        self.setTimeout(() => {
                            EventBus.fire('open-flyout', args);
                        }, 10);
                    }
                }
            },
            [navState]
        )
    );

    useEffect(() => {
        if (navState !== LayersNavState.Layers) {
            setSubMenu({ title: navStateTitles[navState], onBack: () => setNavState(LayersNavState.Layers) });
        }
    }, [navState, setSubMenu]);

    const modifyView = (mutator: (view: UdViewModel) => void) => {
        dispatch(patchFocusedWorkspaceView(mutator));
    };

    const toggleCustomGeojson = (index: number) => {
        modifyView((v) => {
            v.staticData.customGeojson.paths[index].show = !v.staticData.customGeojson.paths[index].show;
        });
    };

    const layers = {
        aircraft: {
            label: 'Aircraft',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.plane.settings.show = e.value;
                }),
            value: view.plane.settings.show,
            fields: [
                {
                    label: 'Scheduled',
                    value: view.plane.settings.showScheduled,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showScheduled = checked))
                },
                {
                    label: 'Planned',
                    value: view.plane.settings.showPlanned,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showPlanned = checked))
                },
                {
                    label: 'Active',
                    value: view.plane.settings.showActive,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showActive = checked))
                },
                {
                    label: 'Ground vehicles',
                    spacingBefore: true,
                    value: view.plane.settings.showGroundVehicles,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showGroundVehicles = checked))
                },
                {
                    label: 'Parked',
                    value: view.plane.settings.showParked,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showParked = checked))
                },
                {
                    label: 'Diversion lines',
                    value: view.plane.settings.showDiversionLines,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.showDiversionLines = checked))
                },
                {
                    label: 'City pair',
                    value: view.plane.settings.cityPair.show,
                    setChecked: (checked) => modifyView((v) => (v.plane.settings.cityPair.show = checked)),
                    fields: [
                        {
                            label: 'Active',
                            value: view.plane.settings.cityPair.active,
                            setChecked: (checked) => modifyView((v) => (v.plane.settings.cityPair.active = checked))
                        },
                        {
                            label: 'Planned',
                            value: view.plane.settings.cityPair.planned,
                            setChecked: (checked) => modifyView((v) => (v.plane.settings.cityPair.planned = checked))
                        },
                        {
                            label: 'Scheduled',
                            value: view.plane.settings.cityPair.scheduled,
                            setChecked: (checked) => modifyView((v) => (v.plane.settings.cityPair.scheduled = checked))
                        }
                    ]
                }
            ],
            navigateTo: LayersNavState.AircraftSettings
        },
        weather: {
            label: 'Weather',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.weather.show = e.value;
                }),
            value: view.weather.show,
            navigateTo: LayersNavState.WeatherSettings
        },
        airport: {
            label: 'Airport',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.airport.show = e.value;
                }),
            defaultColor: [{ type: 'maplibre-global-state', value: 'feNav_icon-color' }],
            value: view.staticData.airport.show,
            fields: [
                {
                    label: 'My airports',
                    value: view.staticData.airport.myAirports,
                    setChecked: (show) => modifyView((x) => (x.staticData.airport.myAirports.show = show)),
                    setColor: (color) => modifyView((x) => (x.staticData.airport.myAirports.color = color))
                },
                {
                    label: 'Airport (tower)',
                    value: view.staticData.airport.tower,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.tower.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airport.tower.color = color))
                },
                {
                    label: 'Airport (no tower)',
                    value: view.staticData.airport.noTower,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.noTower.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airport.noTower.color = color))
                },
                {
                    label: 'Heliport',
                    value: view.staticData.airport.heliport,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.heliport.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airport.heliport.color = color))
                },
                {
                    label: 'Seaplane',
                    value: view.staticData.airport.seaplane,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.seaplane.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airport.seaplane.color = color))
                },
                {
                    label: 'Other',
                    value: view.staticData.airport.others,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.others.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airport.others.color = color))
                },
                {
                    label: 'Deicing',
                    spacingBefore: true,
                    value: view.staticData.airport.deicing,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.deicing = checked))
                },
                {
                    label: 'Runways',
                    value: view.staticData.airport.runways,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airport.runways = checked))
                }
            ],
            filterGroups: {
                colored: true,
                get: (view) => view.staticData.airport.filterGroup,
                type: FilterGroupType.airport
            },
            navigateTo: LayersNavState.AirportSettings
        },
        airspace: {
            label: 'Airspace',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.airspace.show = e.value;
                }),
            value: view.staticData.airspace.show,
            fields: [
                {
                    label: 'FIR',
                    value: view.staticData.airspace.fir,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.fir = checked))
                },
                {
                    label: 'UIR',
                    value: view.staticData.airspace.uir,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.uir = checked))
                },
                {
                    label: 'Class A',
                    value: view.staticData.airspace.classA,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classA = checked))
                },
                {
                    label: 'Class B',
                    value: view.staticData.airspace.classB,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classB = checked))
                },
                {
                    label: 'Class C',
                    value: view.staticData.airspace.classC,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classC = checked))
                },
                {
                    label: 'Class D',
                    value: view.staticData.airspace.classD,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classD = checked))
                },
                {
                    label: 'Class E',
                    value: view.staticData.airspace.classE,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classE = checked))
                },
                {
                    label: 'Class F',
                    value: view.staticData.airspace.classF,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classF = checked))
                },
                {
                    label: 'Class G',
                    value: view.staticData.airspace.classG,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.classG = checked))
                },
                {
                    label: 'Undefined',
                    value: view.staticData.airspace.other,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airspace.other = checked))
                }
            ],
            filterGroups: {
                get: (x) => x.staticData.airspace.filterGroup,
                type: FilterGroupType.airspace
            }
        },
        airways: {
            label: 'Airways',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.airway.show = e.value;
                }),
            navigateTo: LayersNavState.AirwaysSettings,
            value: view.staticData.airway.show,
            defaultColor: [
                { type: 'literal', color: 'rgb(0,0,160)' },
                { type: 'maplibre-global-state', value: 'feNav_line-color_awy_def' }
            ],
            fields: [
                {
                    label: 'High airways',
                    value: view.staticData.airway.high,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airway.high.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airway.high.color = color))
                },
                {
                    label: 'Low airways',
                    value: view.staticData.airway.low,
                    setChecked: (checked) => modifyView((v) => (v.staticData.airway.low.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.airway.low.color = color))
                }
            ],
            filterGroups: {
                colored: true,
                get: (x) => x.staticData.airway.filterGroup,
                type: FilterGroupType.airway
            }
        },
        drawingObjects: {
            label: 'Drawing objects',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.drawingObjects.show = e.value;
                }),
            value: view.staticData.drawingObjects.show,
            fields: [
                {
                    label: 'Alert areas',
                    value: view.staticData.drawingObjects.alert,
                    setChecked: (checked) => modifyView((v) => (v.staticData.drawingObjects.alert = checked))
                },
                {
                    label: 'Avoidance areas',
                    value: view.staticData.drawingObjects.avoidance,
                    setChecked: (checked) => modifyView((v) => (v.staticData.drawingObjects.avoidance = checked))
                }
            ]
        },
        fixes: {
            label: 'Fixes',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.fixes.show = e.value;
                }),
            value: view.staticData.fixes.show,
            navigateTo: LayersNavState.FixesSettings,
            defaultColor: [{ type: 'maplibre-global-state', value: 'feNav_icon-color' }],
            fields: [
                {
                    label: 'Lat/Lon',
                    value: view.staticData.fixes.latLon,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.latLon.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.latLon.color = color))
                },
                {
                    label: 'NRS',
                    value: view.staticData.fixes.nrs,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.nrs.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.nrs.color = color))
                },
                {
                    label: 'AACAA/SUA',
                    value: view.staticData.fixes.aacaA_SUA,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.aacaA_SUA.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.aacaA_SUA.color = color))
                },
                {
                    label: 'Intersection',
                    value: view.staticData.fixes.intersection,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.intersection.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.intersection.color = color))
                },
                {
                    label: 'RNAV',
                    value: view.staticData.fixes.rnav,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.rnav.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.rnav.color = color))
                },
                {
                    label: 'Terminal',
                    value: view.staticData.fixes.terminal,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.terminal.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.terminal.color = color))
                },
                {
                    label: 'VFR',
                    value: view.staticData.fixes.vfr,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.vfr.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.vfr.color = color))
                },
                {
                    label: 'Other',
                    value: view.staticData.fixes.other,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.other.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.fixes.other.color = color))
                },
                {
                    label: 'Low altitude',
                    spacingBefore: true,
                    value: view.staticData.fixes.low,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.low = checked))
                },
                {
                    label: 'High altitude',
                    value: view.staticData.fixes.high,
                    setChecked: (checked) => modifyView((v) => (v.staticData.fixes.high = checked))
                }
            ],
            filterGroups: {
                colored: true,
                type: FilterGroupType.fix,
                get: (x) => x.staticData.fixes.filterGroup
            }
        },
        serviceStations: {
            label: 'Flight service stations',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.flightServiceStation.show = e.value;
                }),
            value: view.staticData.flightServiceStation.show,
            filterGroups: {
                get: (x) => x.staticData.flightServiceStation.filterGroup,
                type: FilterGroupType.flightServiceStation
            }
        },
        holdingPatterns: {
            label: 'Holding patterns',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.holding.show = e.value;
                }),
            value: view.staticData.holding.show,
            fields: [
                {
                    label: 'Low altitude',
                    value: view.staticData.holding.lowAltitude,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.lowAltitude = checked))
                },
                {
                    label: 'High altitude',
                    value: view.staticData.holding.highAltitude,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.highAltitude = checked))
                },
                {
                    label: 'SID',
                    value: view.staticData.holding.sid,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.sid = checked))
                },
                {
                    label: 'STAR',
                    value: view.staticData.holding.star,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.star = checked))
                },
                {
                    label: 'Approach',
                    value: view.staticData.holding.approach,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.approach = checked))
                },
                {
                    label: 'Missed approach',
                    value: view.staticData.holding.missedApproach,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.missedApproach = checked))
                },
                {
                    label: 'Undefined',
                    value: view.staticData.holding.other,
                    setChecked: (checked) => modifyView((v) => (v.staticData.holding.other = checked))
                }
            ],
            filterGroups: {
                get: (x) => x.staticData.holding.filterGroup,
                type: FilterGroupType.holdingPattern
            }
        },
        latLon: {
            label: 'Lat/Lon graticule',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.latLon.show = e.value;
                }),
            value: view.staticData.latLon.show
        },
        gridMora: {
            label: 'Grid MORA',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.gridMora.show = e.value;
                }),
            value: view.staticData.gridMora.show
        },
        navaids: {
            label: 'Navaids',
            navigateTo: LayersNavState.NavaidsSettings,
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.navaids.show = e.value;
                }),
            value: view.staticData.navaids.show,
            defaultColor: [{ type: 'maplibre-global-state', value: 'feNav_icon-color' }],
            fields: [
                {
                    label: 'NDB',
                    value: view.staticData.navaids.ndb,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.ndb.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.ndb.color = color))
                },
                {
                    label: 'VOR',
                    value: view.staticData.navaids.vor,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.vor.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.vor.color = color))
                },
                {
                    label: 'VOR/DME',
                    value: view.staticData.navaids.voR_DME,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.voR_DME.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.voR_DME.color = color))
                },
                {
                    label: 'VORTAC',
                    value: view.staticData.navaids.vortac,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.vortac.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.vortac.color = color))
                },
                {
                    label: 'TACAN',
                    value: view.staticData.navaids.tacan,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.tacan.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.tacan.color = color))
                },
                {
                    label: 'DME',
                    value: view.staticData.navaids.dme,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.dme.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.dme.color = color))
                },
                {
                    label: 'NDB/DME',
                    value: view.staticData.navaids.ndB_DME,
                    setChecked: (checked) => modifyView((v) => (v.staticData.navaids.ndB_DME.show = checked)),
                    setColor: (color) => modifyView((v) => (v.staticData.navaids.ndB_DME.color = color))
                }
            ],
            filterGroups: {
                colored: true,
                get: (x) => x.staticData.navaids.filterGroup,
                type: FilterGroupType.navaid
            }
        },
        overwaterAreas: {
            label: 'Overwater areas',
            value: view.staticData.overwaterAreas.show,
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.overwaterAreas.show = e.value;
                }),
            navigateTo: LayersNavState.OverwaterAreasSettings
        },
        sids: {
            label: 'SIDs',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.sids.show = e.value;
                }),
            value: view.staticData.sids.show,
            filterGroups: {
                get: (x) => x.staticData.sids.filterGroup,
                type: FilterGroupType.sid
            }
        },
        stars: {
            label: 'STARs',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.stars.show = e.value;
                }),
            value: view.staticData.stars.show,
            filterGroups: {
                get: (x) => x.staticData.stars.filterGroup,
                type: FilterGroupType.star
            }
        },
        sua: {
            label: 'Special Use Airspace (SUA)',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.sua.show = e.value;
                }),
            value: view.staticData.sua.show,
            fields: [
                {
                    label: 'Static',
                    value: view.staticData.sua.static,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.static = checked))
                },
                {
                    label: 'Dynamic',
                    value: view.staticData.sua.dynamic,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.dynamic = checked))
                },
                {
                    label: 'Alert',
                    spacingBefore: true,
                    value: view.staticData.sua.alert,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.alert = checked))
                },
                {
                    label: 'Caution',
                    value: view.staticData.sua.caution,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.caution = checked))
                },
                {
                    label: 'Danger',
                    value: view.staticData.sua.danger,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.danger = checked))
                },
                {
                    label: 'Military area',
                    value: view.staticData.sua.militaryArea,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.militaryArea = checked))
                },
                {
                    label: 'Prohibited',
                    value: view.staticData.sua.prohibited,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.prohibited = checked))
                },
                {
                    label: 'Restricted',
                    value: view.staticData.sua.restricted,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.restricted = checked))
                },
                {
                    label: 'Training',
                    value: view.staticData.sua.traning,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.traning = checked))
                },
                {
                    label: 'Warning',
                    value: view.staticData.sua.warning,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.warning = checked))
                },
                {
                    label: 'Unspecified/unknown',
                    value: view.staticData.sua.unkown,
                    setChecked: (checked) => modifyView((v) => (v.staticData.sua.unkown = checked))
                }
            ],
            filterGroups: {
                type: FilterGroupType.sua,
                get: (x) => x.staticData.sua.filterGroup
            },
            navigateTo: LayersNavState.SuaSettings
        },
        timeZones: {
            label: 'Time zones',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.timeZone.show = e.value;
                }),
            value: view.staticData.timeZone.show
        },
        weatherStations: {
            label: 'Weather stations',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.weatherStation.show = e.value;
                }),
            value: view.staticData.weatherStation.show,
            filterGroups: {
                get: (x) => x.staticData.weatherStation.filterGroup,
                type: FilterGroupType.weatherStation
            }
        },
        customGeojson: {
            label: 'Custom',
            onChange: (e: CheckboxChangeEvent) =>
                modifyView((v) => {
                    v.staticData.customGeojson.show = e.value;
                }),
            value: view.staticData.customGeojson.show,
            navigateTo: LayersNavState.CustomGeojsonSettings
        }
    } satisfies Record<string, LayerConfig>;

    let settings: ReactNode | null;
    switch (navState) {
        case LayersNavState.AircraftSettings:
            settings = <AircraftSettings />;
            break;
        case LayersNavState.DayNightShadingSettings:
            settings = <DayNightShadingSettings />;
            break;
        case LayersNavState.TerrainShadingSettings:
            settings = <TerrainShadingSettings />;
            break;
        case LayersNavState.WeatherSettings:
            settings = <WeatherSettings />;
            break;
        case LayersNavState.AirportSettings:
            settings = <AirportLayerSettings />;
            break;
        case LayersNavState.FixesSettings:
            settings = <FixesSettings />;
            break;
        case LayersNavState.AirwaysSettings:
            settings = <AirwaysSettings />;
            break;
        case LayersNavState.SuaSettings:
            settings = <DsuaMenu />;
            break;
        case LayersNavState.CustomGeojsonSettings:
            settings = <CustomGeojsonSettings />;
            break;
        case LayersNavState.NavaidsSettings:
            settings = <NavaidsSettings />;
            break;
        case LayersNavState.OverwaterAreasSettings:
            settings = <OverwaterAreasSettings />;
            break;
        default:
            settings = null;
            break;
    }

    return (
        <>
            {settings}

            <div className={clsx('overflow-auto', navState !== LayersNavState.Layers && 'd-none')} id="layers-flyout">
                <div className="p-3 pe-auto d-flex flex-column gap-1" style={{ minWidth: 200 }}>
                    <div
                        className="d-grid gap-2"
                        style={{
                            gridTemplateColumns: 'max-content minmax(0, 1fr)',
                            alignItems: 'center',
                            width: '100%',
                            minWidth: 0
                        }}>
                        <span style={{ whiteSpace: 'nowrap' }}>Background map</span>
                        <DropDownList
                            id="layers-map-background"
                            popupSettings={{
                                width: '400px',
                                height: '600px',
                                anchorAlign: { horizontal: 'right', vertical: 'bottom' },
                                popupAlign: { horizontal: 'right', vertical: 'top' }
                            }}
                            style={{ width: '100%', minWidth: 0 }}
                            data={[mapStyles.find((style) => style.value === view.map.style)]}
                            textField="label"
                            onChange={(e) =>
                                modifyView((v) => {
                                    v.map.style = e.target.value.value;
                                })
                            }
                            value={mapStyles.find((x) => x.value === view.map.style)}
                            itemRender={() => (
                                <div
                                    className="p-2 w-100 h-100"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr 1fr',
                                        gridRowGap: '.5rem',
                                        gridColumnGap: '.5rem'
                                    }}>
                                    {mapStyles.map((style) => (
                                        <div
                                            key={style.value}
                                            className={clsx('position-relative d-flex flex-column justify-content-end', 'map-style-item', {
                                                selected: view.map.style === style.value
                                            })}
                                            onClick={() =>
                                                dispatch(
                                                    patchFocusedWorkspaceView((v) => {
                                                        v.map.style = style.value;
                                                    })
                                                )
                                            }>
                                            <img alt={`Map style ${style.label}`} src={style.image} />
                                            <div className="flex-shrink-0">{style.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />

                        <span style={{ whiteSpace: 'nowrap' }}>Projection</span>
                        <RadioGroup
                            id="layers-projection"
                            data={projectionTypes}
                            value={view.map.useGlobeView ? 'globe' : 'mercator'}
                            layout={'horizontal'}
                            style={{ minWidth: 0 }}
                            onChange={(e) =>
                                modifyView((v) => {
                                    v.map.useGlobeView = e.value === 'globe';
                                })
                            }
                        />

                        {view.map.useGlobeView && (
                            <>
                                <Checkbox
                                    label={'Auto-rotate'}
                                    checked={view.map.globeAutoRotate}
                                    onChange={(e) =>
                                        modifyView((v) => {
                                            v.map.globeAutoRotate = e.value;
                                        })
                                    }
                                />
                                <div
                                    className="d-flex align-items-center gap-1 w-100"
                                    style={{ minWidth: 0 }}
                                    onPointerUp={() => {
                                        if (draftRotationSpeed !== null) {
                                            modifyView((v) => {
                                                v.map.globeAutoRotateSpeed = draftRotationSpeed;
                                            });
                                            setDraftRotationSpeed(null);
                                        }
                                    }}>
                                    <SliderWithTooltip
                                        min={0}
                                        max={10}
                                        step={1}
                                        className="flex-grow-1 w-100"
                                        style={{ minWidth: 0 }}
                                        disabled={!view.map.globeAutoRotate}
                                        value={draftRotationSpeed ?? view.map.globeAutoRotateSpeed}
                                        onChange={(e) => setDraftRotationSpeed(e.value)}
                                    />
                                </div>
                            </>
                        )}

                        <span style={{ whiteSpace: 'nowrap' }}>Brightness</span>
                        <div className="d-flex align-items-center gap-1 w-100" style={{ minWidth: 0 }}>
                            <SvgIcon icon={ClearSunnyOutline} size={'large'} />
                            <SliderWithTooltip
                                min={0}
                                max={255}
                                step={1}
                                className="flex-grow-1 w-100"
                                style={{ minWidth: 0 }}
                                value={view.staticData.miscellaneous.backGroundBrightness}
                                onChange={(e) =>
                                    modifyView((v) => {
                                        v.staticData.miscellaneous.backGroundBrightness = Math.floor(e.value);
                                    })
                                }
                            />
                            <SvgIcon icon={ClearSunnySolid} size={'large'} />
                        </div>
                    </div>

                    <div>
                        <div className="d-flex justify-content-between align-items-center" id="layers-day-night">
                            <Checkbox
                                label={'Day/night shading'}
                                checked={view.map.dayNightShading}
                                onChange={(e) =>
                                    modifyView((v) => {
                                        v.map.dayNightShading = e.value;
                                    })
                                }></Checkbox>
                            <Button
                                id="layers-day-night-expand"
                                svgIcon={ChevronRightOutline}
                                fillMode={'flat'}
                                className="button-small menu-button"
                                onClick={() => setNavState(LayersNavState.DayNightShadingSettings)}></Button>
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                            <Checkbox
                                label="Terrain shading"
                                checked={view.map.terrainShading.enabled}
                                onChange={(e) => modifyView((v) => (v.map.terrainShading.enabled = e.value))}
                            />
                            <Button
                                className="button-small menu-button"
                                svgIcon={ChevronRightOutline}
                                fillMode="flat"
                                onClick={() => setNavState(LayersNavState.TerrainShadingSettings)}
                            />
                        </div>
                    </div>

                    <div className="border-top"></div>

                    <div className="d-flex flex-column gap-1" id="layers-list">
                        <LayersSection layerConfig={layers.aircraft} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.weather} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.airport} onNavigate={setNavState}>
                            {navDataWarning && (
                                <div className="text-error d-flex ps-3" style={{ maxWidth: 200 }}>
                                    <div className="nest-1"></div>
                                    <div>{navDataWarning}</div>
                                </div>
                            )}
                        </LayersSection>

                        <LayersSection layerConfig={layers.airspace} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.airways} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.drawingObjects} onNavigate={setNavState}>
                            <div className="menu-item">
                                <div className="nest-1">
                                    <Button
                                        svgIcon={showDrawingObjectsViewBasedMenu ? ChevronDownOutline : ChevronRightOutline}
                                        fillMode={'flat'}
                                        className={'button-small menu-button float-end'}
                                        onClick={() => setShowDrawingObjectsViewBasedMenu(!showDrawingObjectsViewBasedMenu)}
                                    />
                                </div>
                                <Checkbox
                                    label={'View based'}
                                    checked={view.staticData.drawingObjects.view}
                                    onChange={(e) =>
                                        modifyView((v) => {
                                            v.staticData.drawingObjects.view = e.value;
                                        })
                                    }></Checkbox>
                            </div>
                            {showDrawingObjectsViewBasedMenu && (
                                <div>
                                    {drawingObjects
                                        .filter((o) => o.category === DrawingObjectCategory.viewBased)
                                        ?.map((drawingObject) => (
                                            <div className={'menu-item'} key={drawingObject.id}>
                                                <div className={'nest-2'}></div>
                                                <Checkbox
                                                    label={drawingObject.name}
                                                    checked={view.staticData.drawingObjects.viewObjectIds.includes(drawingObject.id)}
                                                    onChange={(e) => {
                                                        const isChecked = e.value;
                                                        modifyView((v) => {
                                                            if (isChecked) {
                                                                if (!v.staticData.drawingObjects.viewObjectIds.includes(drawingObject.id)) {
                                                                    v.staticData.drawingObjects.viewObjectIds.push(drawingObject.id);
                                                                }
                                                            } else {
                                                                v.staticData.drawingObjects.viewObjectIds =
                                                                    v.staticData.drawingObjects.viewObjectIds.filter(
                                                                        (id) => id !== drawingObject.id
                                                                    );
                                                            }
                                                        });
                                                    }}></Checkbox>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </LayersSection>

                        <LayersSection layerConfig={layers.fixes} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.serviceStations} onNavigate={setNavState}>
                            <div className={'menu-item'}>
                                <div className={'nest-1'}></div>
                                <Checkbox
                                    label={'All'}
                                    checked={!view.staticData.flightServiceStation.filterGroup.show}
                                    onChange={(e) => {
                                        modifyView((x) => (x.staticData.flightServiceStation.filterGroup.show = !e.value));
                                    }}></Checkbox>
                            </div>
                        </LayersSection>

                        <LayersSection layerConfig={layers.holdingPatterns} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.latLon} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.gridMora} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.navaids} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.overwaterAreas} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.sids} onNavigate={setNavState}>
                            <div className={'menu-item'}>
                                <div className={'nest-1'}></div>
                                <Checkbox
                                    label={'All'}
                                    checked={!view.staticData.sids.filterGroup.show}
                                    onChange={(e) => {
                                        modifyView((x) => (x.staticData.sids.filterGroup.show = !e.value));
                                    }}></Checkbox>
                            </div>
                        </LayersSection>

                        <LayersSection layerConfig={layers.stars} onNavigate={setNavState}>
                            <div className={'menu-item'}>
                                <div className="nest-1"></div>
                                <Checkbox
                                    label={'All'}
                                    checked={!view.staticData.stars.filterGroup.show}
                                    onChange={(e) => {
                                        modifyView((x) => (x.staticData.stars.filterGroup.show = !e.value));
                                    }}></Checkbox>
                            </div>
                        </LayersSection>

                        <LayersSection layerConfig={layers.sua} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.timeZones} onNavigate={setNavState} />

                        <LayersSection layerConfig={layers.weatherStations} onNavigate={setNavState}>
                            <div className={'menu-item'}>
                                <div className={'nest-1'}></div>
                                <Checkbox
                                    label={'All'}
                                    checked={!view.staticData.weatherStation.filterGroup.show}
                                    onChange={(e) => {
                                        modifyView((x) => (x.staticData.weatherStation.filterGroup.show = !e.value));
                                    }}></Checkbox>
                            </div>
                        </LayersSection>

                        <LayersSection layerConfig={layers.customGeojson} onNavigate={setNavState} id="flyout-layers-custom">
                            <div className={'d-flex flex-column'}>
                                {view.staticData.customGeojson.paths.map((path, index) => (
                                    <div key={path.name} className={'menu-item'}>
                                        <div key={`placeholder-${path.name}`} className={'nest-1'}></div>
                                        <div className={'d-flex align-items-center gap-1'} style={{ minWidth: 0, maxWidth: '190px' }}>
                                            <Checkbox
                                                key={`checkbox-${path.name}`}
                                                checked={path.show}
                                                onClick={() => toggleCustomGeojson(index)}
                                            />
                                            <div
                                                className={'d-flex align-items-center gap-1'}
                                                style={{ minWidth: 0, flex: 1 }}
                                                onClick={() => toggleCustomGeojson(index)}>
                                                <SvgIcon
                                                    icon={path.type === 'file' ? FileOutline : LinkOutline}
                                                    size={'small'}
                                                    className={'flex-shrink-0'}
                                                />
                                                <div className={'text-truncate'} style={{ minWidth: 0, flex: 1 }}>
                                                    {path.name}
                                                </div>
                                                {geojsonWarnings.some((w) => w.index === index) && (
                                                    <SvgIcon
                                                        icon={WarningTriangleOutline}
                                                        size={'small'}
                                                        className={'text-warning flex-shrink-0'}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {view.staticData.customGeojson.paths.length === 0 && (
                                    <div>
                                        <div className={'secondary-text'}>No custom GeoJSON files found.</div>
                                        <div className={'secondary-text'}>
                                            <span>You can add them </span>
                                            <a
                                                className={'text-decoration-underline cursor-pointer'}
                                                onClick={() => setNavState(LayersNavState.CustomGeojsonSettings)}>
                                                here
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </LayersSection>
                    </div>
                </div>
            </div>
        </>
    );
}
